"use client";

import { MainContainer } from "@/components/Containers";
import { Flex } from "@/components/Flex";
import { EditorContextProvider } from "@/components/FullPageEditor/hooks/useEditorContextSelector";
import NoteChatInterface from "@/components/NotePage/components/NoteChatInterface";
import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import br from "@/styles/breakpoints.module.css";
import { spacing } from "@/utils/spacing";
import { getHtmlFromContent } from "@knowt/editor/helpers/getHtmlFromContent";
import { ItemType, Note } from "@knowt/syncing/graphql/schema";
import { useNote } from "@knowt/syncing/hooks/notes/useNotes";
import { StableSWRKeys } from "@knowt/syncing/hooks/swr/swr";
import { LocalUser } from "@knowt/syncing/hooks/user/types";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ComponentProps } from "react";
import { SWRConfig, unstable_serialize } from "swr";
import AINotePreview from "../AI/AINoteGenerationPage/components/AINotePreview";
import { ChatEditorCommunicatorProvider } from "../AI/KaiChat/hooks/useChatEditorCommunicator";
import { resolveNoteSWRKey } from "@knowt/syncing/hooks/notes/utils";

const SWRWrappedContent = ({
    serverUser,
    serverNote,
}: {
    serverUser: LocalUser;
    serverNote: Note;
}) => {
    return (
        <SWRConfig
            value={{
                fallback: {
                    [StableSWRKeys.USER]: serverUser,
                    [unstable_serialize(
                        resolveNoteSWRKey({ userId: serverUser?.user?.ID || "", noteId: serverNote?.noteId })
                    )]: serverNote,
                },
            }}>
            <AIGeneratedTeacherToolNote noteId={serverNote.noteId} />
        </SWRConfig>
    );
};

const AIGeneratedTeacherToolNote = ({ noteId }: { noteId: string }) => {
    const { mdDown } = useBreakPoints();

    const isShortScreenHeight = useMediaQuery(
        "(max-height: 800px) and (max-width: 900px), (max-height: 600px) and (max-width: 1170px), (max-height: 700px) and (max-width: 1400px), (max-height: 800px) and (min-width: 1410px)"
    );

    const { note, update } = useNote({
        noteId: noteId,
    });

    const mainContainerStyles: ComponentProps<typeof MainContainer>["style"] = {
        marginBlock: `3rem 3rem`,
        marginInline: `3rem 3rem`,
    };

    const saveContent = async ({
        content,
        title,
        ...overrides
    }: { content: string; title?: string; draft?: boolean }) => {
        try {
            await update({
                title,
                content: getHtmlFromContent({
                    content,
                    type: "note",
                }),
                ...overrides,
            });
        } catch (e) {
            // biome-ignore lint/suspicious/noConsole: <explanation>
            console.log("update Error : ", e);
        }
    };

    return (
        <MainContainer style={mainContainerStyles}>
            <EditorContextProvider
                content={getHtmlFromContent({ content: note?.content || "", type: "note" })}
                editable={false}
                disabledCustomPlaceholder={true}
                saveContent={
                    (async (content: string) => await saveContent({ content })) as unknown as (
                        content: string
                    ) => Promise<void>
                }>
                <ChatEditorCommunicatorProvider itemId={note?.noteId} itemType={ItemType.NOTE} isOwner>
                    <Flex
                        style={{
                            gap: spacing.MD,
                            maxWidth: "100%",
                            width: "100vw",
                            justifyContent: "space-between",
                            alignItems: "stretch",
                            height: !mdDown ? "calc(100vh - var(--navbar-height) - 6rem)" : undefined,
                        }}
                        className={br.mdDownColumn}>
                        <AINotePreview
                            isTeacherTool
                            generatedContent={note?.content}
                            noteId={note?.noteId}
                            noteTitle={note?.title}
                            isGenerating={false}
                            containerStyle={
                                isShortScreenHeight
                                    ? {
                                          maxHeight: "80rem",
                                      }
                                    : {
                                          maxHeight: "undefined",
                                      }
                            }
                        />

                        <Flex
                            className={br.mdDownWidth100}
                            style={{
                                overflow: "hidden",
                                flex: 1,
                                width: "50%",
                                height: "100%",
                                minHeight: "60vh",
                                borderRadius: "2rem",
                                maxHeight: isShortScreenHeight ? (mdDown ? undefined : "80rem") : "unset",
                            }}>
                            <NoteChatInterface
                                key={note?.content}
                                containerStyle={{
                                    maxHeight: isShortScreenHeight
                                        ? "80rem"
                                        : !mdDown
                                          ? "calc(100vh - var(--navbar-height))"
                                          : undefined,
                                    minHeight: mdDown ? "unset" : undefined,
                                }}
                                note={note}
                                disabled={false}
                                disabledReason="Please wait for kai to finish generating your note."
                            />
                        </Flex>
                    </Flex>
                </ChatEditorCommunicatorProvider>
            </EditorContextProvider>
        </MainContainer>
    );
};

export default SWRWrappedContent;
